import * as React from 'react'

import {
  Box,
  Button,
  CheckBox,
  SearchField,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  BulkAssignLocationsDocument,
  ProfileEmployeeLocationEmployeesLocationFragment,
  ProfileEmployeeLocationFragment,
} from 'generated/resources/graphql'
import { difference, orderBy, xor } from 'lodash'

import BoxList from 'modules/core/components/BoxList'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import ScrollableList from 'modules/core/components/ScrollableList'
import createComputed from 'modules/core/hooks/createComputed'
import { useMutation } from 'modules/core/hooks/useMutation'
import useSearch from 'modules/core/hooks/useSearch'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

type SortDirection = 'asc' | 'desc'

interface AssignWorkplacesModalProps {
  companyLocations: ProfileEmployeeLocationFragment[]
  employeeDefaultLocationId: string
  employeeId: string
  employeeLocations: ProfileEmployeeLocationEmployeesLocationFragment[]
  onCloseModal: () => void
}

const useComputedLocations = createComputed(
  (
    locationsArr: ProfileEmployeeLocationFragment[],
    search: string,
    order: SortDirection
  ): ProfileEmployeeLocationFragment[] => {
    const locationsToDisplay = locationsArr.filter((location) => {
      return matchText(search, location.name)
    })

    return orderBy(locationsToDisplay, (location) => location.name, [order])
  }
)

const AssignWorkplacesModal: React.FC<AssignWorkplacesModalProps> = ({
  companyLocations,
  employeeDefaultLocationId,
  employeeId,
  employeeLocations,
  onCloseModal,
}) => {
  const employeeLocationIds = employeeLocations
    .map((location) => location?.location?.id)
    .filter((id): id is string => id != null)
  const locationIds = companyLocations.map((location) => location.id)
  const { search, onSearch } = useSearch()
  const [selectedIds, setSelectedIds] = React.useState(() => {
    const ids = new Set(employeeLocationIds)
    if (employeeDefaultLocationId && employeeDefaultLocationId !== '0') ids.add(employeeDefaultLocationId)
    return Array.from(ids)
  })
  const [order, setOrder] = React.useState<SortDirection>('asc')
  const selectedAll = !difference(locationIds, selectedIds).length
  const [bulkAssignLocations] = useMutation(BulkAssignLocationsDocument, {
    extractorFn: (data) => data.locationsMutations.bulkAssignLocationsEmployeesLocation,
    update: (cache, { data }) => {
      const employeesLocations =
        data?.locationsMutations?.bulkAssignLocationsEmployeesLocation?.employeesLocations
      if (employeesLocations && employeeId) {
        // Extract locations from the response
        // Each employeesLocations item has a location object with id and name
        const assignedLocations = employeesLocations.map((el) => el.location)

        // Update the employee's locations array in the cache
        cache.modify({
          id: cache.identify({ __typename: 'EmployeesEmployee', id: employeeId }),
          fields: {
            locations() {
              // Create new locations array based on the assigned locations
              // The structure should be: [{ location: { __ref: 'LocationsLocation:ID' } }]
              // The locations are already written to cache by Apollo, so we can identify them
              return assignedLocations
                .map((location) => {
                  if (!location || !location.id) return null
                  return {
                    location: {
                      __ref: cache.identify({ __typename: 'LocationsLocation', id: location.id }),
                    },
                  }
                })
                .filter(Boolean)
            },
          },
        })
      }
    },
  })

  const onSelectWorkplace = (workplaceId: string) => {
    setSelectedIds(xor(selectedIds, [workplaceId]))
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!selectedIds.length) return

    const locationIdsToSend =
      employeeDefaultLocationId && employeeDefaultLocationId !== '0'
        ? Array.from(new Set([...selectedIds, employeeDefaultLocationId]))
        : selectedIds

    const result = await bulkAssignLocations({
      variables: {
        employeeId: employeeId,
        locationIds: locationIdsToSend,
      },
    })

    const handledResult = handleResult(result, (error) => {
      if (error) return
    })

    if (handledResult.success) {
      closePortal()
    }
  }

  const onClickSort = () => {
    order === 'asc' ? setOrder('desc') : setOrder('asc')
  }
  const onSelectAll = () =>
    selectedAll ? setSelectedIds([employeeDefaultLocationId]) : setSelectedIds(locationIds)

  const count = selectedIds.length
  const computedLocations = useComputedLocations(companyLocations, search, order).get()

  return (
    <Modal
      illustration="building"
      title={i18n.t('employee_form.workplaces.assign_workplaces')}
      description={i18n.t('employee_form.workplaces.assign_workplaces_description')}
      onSubmit={onSubmit}
      onClose={onCloseModal}
      footer={
        <Button disabled={!selectedIds.length && !employeeLocationIds.length} submit>
          {i18n.tp('employee_form.workplaces.assign_cta.count', {
            count,
          })}
        </Button>
      }
    >
      <BoxList>
        <Box width="full" flexDirection="row" background="grey200" gap="s8" padding="s8">
          <Box flexGrow>
            <SearchField
              variant="big"
              search={search}
              onSearch={onSearch}
              placeholder={i18n.t('employee_form.teams_info.modal.search')}
            />
          </Box>
        </Box>
        <Box
          alignItems="center"
          background="grey200"
          flexDirection="row"
          gap="s16"
          paddingX="s16"
          paddingY="s12"
          width="full"
        >
          <CheckBox onClick={() => onSelectAll()} checked={selectedAll} />
          <Table.SortingHeader onClick={onClickSort} selected={order === 'asc'}>
            {i18n.t('common.name')}
          </Table.SortingHeader>
        </Box>
        <ScrollableList size="medium">
          {computedLocations.map((location) => {
            const isLocationDefault = employeeDefaultLocationId == location.id
            return (
              <Box
                flexDirection="row"
                key={location.id}
                alignItems="center"
                background="grey000White"
                cursor="pointer"
                gap="s16"
                paddingX="s16"
                paddingY="s12"
                width="full"
              >
                <CheckBox
                  onClick={() => onSelectWorkplace(location.id)}
                  checked={!!selectedIds.find((id) => id === location.id)}
                  disabled={isLocationDefault}
                />
                <Box onClick={() => onSelectWorkplace(location.id)}>
                  <Text.Normal weight="medium">{location.name}</Text.Normal>
                </Box>
              </Box>
            )
          })}
        </ScrollableList>
      </BoxList>
    </Modal>
  )
}

export default AssignWorkplacesModal
